import type { LucideIcon } from 'lucide-react';
import { Check, Copy, Edit, MoreVertical, Trash2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Handle, Position, useReactFlow } from 'reactflow';
import { useClipboard } from '../../contexts/ClipboardContext';
import { useMenu } from '../../contexts/MenuContext';
import { isNodeComplete } from '../../lib/node-validation';
import { cn } from '../../lib/utils';
import type { CustomNodeProps } from '../../types/nodes';
import { NodeDrawer } from '../NodeDrawer';

interface BaseNodeComponentProps extends CustomNodeProps {
	icon: LucideIcon;
	canHaveInputs?: boolean;
	canHaveOutputs?: boolean;
	configComponent?: React.ComponentType<{
		nodeId: string;
		data: CustomNodeProps['data'];
		onChange: (data: CustomNodeProps['data']) => void;
		onWizardStateChange?: React.Dispatch<
			React.SetStateAction<{
				wizardMode: boolean;
				currentStep: number;
				totalSteps: number;
				canProceedToNext: boolean;
				onPreviousStep: () => void;
				onNextStep: () => void;
				onCompleteWizard: () => Promise<void>;
				validationAttempted: boolean;
				validationFailed: boolean;
				isCompletingSetup: boolean;
			}>
		>;
		onValidationRequest?: (validate: () => Promise<boolean>) => void;
		onClose?: () => void;
	}>;
}

const categoryStyles = {
	'flow-control': {
		border: 'border-l-emerald-500',
		background: 'bg-emerald-50 dark:bg-emerald-900/20',
		icon: 'bg-emerald-100 dark:bg-emerald-800/30',
	},
	processing: {
		border: 'border-l-blue-500',
		background: 'bg-blue-50 dark:bg-blue-900/20',
		icon: 'bg-blue-100 dark:bg-blue-800/30',
	},
	integrations: {
		border: 'border-l-purple-500',
		background: 'bg-purple-50 dark:bg-purple-900/20',
		icon: 'bg-purple-100 dark:bg-purple-800/30',
	},
	triggers: {
		border: 'border-l-emerald-500',
		background: 'bg-emerald-50 dark:bg-emerald-900/20',
		icon: 'bg-emerald-100 dark:bg-emerald-800/30',
	},
	advanced: {
		border: 'border-l-orange-500',
		background: 'bg-orange-50 dark:bg-orange-900/20',
		icon: 'bg-orange-100 dark:bg-orange-800/30',
	},
} as const;

export function BaseNode({
	data,
	id,
	icon: Icon,
	canHaveInputs = true,
	canHaveOutputs = true,
	configComponent: ConfigComponent,
}: BaseNodeComponentProps) {
	const { activeMenu, setActiveMenu } = useMenu();
	const { copyNode } = useClipboard();
	const { setNodes, getNodes, getEdges, setEdges } = useReactFlow();
	const menuRef = useRef<HTMLDivElement>(null);
	const buttonRef = useRef<HTMLButtonElement>(null);
	const [isDrawerOpen, setIsDrawerOpen] = useState(false);
	const [isHelpMenuOpen, setIsHelpMenuOpen] = useState(false);
	const [wizardState, setWizardState] = useState({
		wizardMode: false,
		currentStep: 1,
		totalSteps: 4,
		canProceedToNext: true,
		onPreviousStep: () => {},
		onNextStep: () => {},
		onCompleteWizard: async () => {},
		validationAttempted: false,
		validationFailed: false,
		isCompletingSetup: false,
	});
	const closeTimeoutRef = useRef<number>();
	const validationFunctionRef = useRef<(() => Promise<boolean>) | null>(null);

	// Check if node is complete (reactive to node data changes)
	const nodes = getNodes();
	const edges = getEdges();
	const currentNode = nodes.find(node => node.id === id);

	// Force recalculation of completion status when node data changes
	const [, forceUpdate] = useState({});
	useEffect(() => {
		const handleNodeUpdate = (event: CustomEvent) => {
			if (event.detail?.nodeId === id) {
				forceUpdate({});
			}
		};

		window.addEventListener('workflow-node-updated', handleNodeUpdate as EventListener);
		return () => {
			window.removeEventListener('workflow-node-updated', handleNodeUpdate as EventListener);
		};
	}, [id]);

	const isComplete = currentNode ? isNodeComplete(currentNode, edges, true) : false; // isDraft = true for editor

	// Use the current node data from React Flow to ensure we have the latest data
	const currentData = currentNode?.data || data;

	// Close drawer when wizard completes
	useEffect(() => {
		if (isDrawerOpen && wizardState.wizardMode === false && wizardState.currentStep > 1) {
			// Wizard was completed, close the drawer
			setIsDrawerOpen(false);
		}
	}, [wizardState.wizardMode, wizardState.currentStep, isDrawerOpen]);

	useEffect(() => {
		function handleClickOutside(event: MouseEvent) {
			if (
				menuRef.current &&
				buttonRef.current &&
				!menuRef.current.contains(event.target as Node) &&
				!buttonRef.current.contains(event.target as Node)
			) {
				setActiveMenu(null);
			}
		}

		if (activeMenu === id) {
			document.addEventListener('mousedown', handleClickOutside);
		}

		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, [activeMenu, id, setActiveMenu]);

	// Cleanup timeout on unmount
	useEffect(() => {
		return () => {
			if (closeTimeoutRef.current) {
				clearTimeout(closeTimeoutRef.current);
			}
		};
	}, []);

	const handleMouseLeave = () => {
		if (closeTimeoutRef.current) {
			clearTimeout(closeTimeoutRef.current);
		}
		closeTimeoutRef.current = window.setTimeout(() => {
			setActiveMenu(null);
		}, 300); // 300ms delay before closing
	};

	const handleMouseEnter = () => {
		if (closeTimeoutRef.current) {
			clearTimeout(closeTimeoutRef.current);
		}
	};

	const handleDelete = () => {
		setNodes(nodes => nodes.filter(node => node.id !== id));
		setEdges(edges => edges.filter(edge => edge.source !== id && edge.target !== id));
		setActiveMenu(null);
	};

	const handleEdit = () => {
		setIsDrawerOpen(true);
		setActiveMenu(null);
	};

	const handleCopy = () => {
		const currentNode = getNodes().find(node => node.id === id);
		if (currentNode) {
			copyNode(currentNode);
		}
		setActiveMenu(null);
	};

	const handleChange = (newData: typeof data) => {
		// Update the node data without validation - this is for real-time changes
		setNodes(nodes => {
			const updatedNodes = nodes.map(node =>
				node.id === id ? { ...node, data: newData } : node
			);

			// Trigger a custom event to notify the workflow editor of the change
			// This ensures the onNodeChange callback is triggered and undo state is saved
			setTimeout(() => {
				window.dispatchEvent(
					new CustomEvent('workflow-node-updated', {
						detail: { nodeId: id, newData, shouldSaveState: true },
					})
				);
			}, 0);

			return updatedNodes;
		});
	};

	const handleSave = async (newData: typeof data) => {
		// Debug logging to understand the issue
		console.log('🔍 BaseNode.handleSave - Debug Info:', {
			wizardMode: wizardState.wizardMode,
			onCompleteWizard: !!wizardState.onCompleteWizard,
			validationFunctionRef: !!validationFunctionRef.current,
			nodeId: id,
			nodeType: data.type,
		});

		// If we're in wizard mode and have an onCompleteWizard function, use that instead
		// This ensures the wizard validation pattern is followed
		if (wizardState.wizardMode && wizardState.onCompleteWizard) {
			console.log('🔍 BaseNode.handleSave - Using wizard mode with onCompleteWizard');
			try {
				await wizardState.onCompleteWizard();
				// The wizard validation logic will handle saving and closing
				console.log('🔍 BaseNode.handleSave - Wizard completion successful');
				return;
			} catch (error) {
				console.error('Wizard completion failed:', error);
				return;
			}
		}

		console.log('🔍 BaseNode.handleSave - Using fallback validation logic');
		// Fallback to the old validation logic for non-wizard nodes
		// If there's a validation function, run it first
		if (validationFunctionRef.current) {
			try {
				const isValid = await validationFunctionRef.current();
				if (!isValid) {
					// Don't save if validation failed
					console.log('🔍 BaseNode.handleSave - Validation failed, not saving');
					return;
				}
			} catch (error) {
				console.error('Validation error:', error);
				return;
			}
		}

		console.log('🔍 BaseNode.handleSave - Saving node data');
		// Update the node data after validation passes
		setNodes(nodes => {
			const updatedNodes = nodes.map(node =>
				node.id === id ? { ...node, data: newData } : node
			);

			// Trigger a custom event to notify the workflow editor of the change
			// This ensures the onNodeChange callback is triggered and undo state is saved
			setTimeout(() => {
				window.dispatchEvent(
					new CustomEvent('workflow-node-updated', {
						detail: { nodeId: id, newData, shouldSaveState: true },
					})
				);
			}, 0);

			return updatedNodes;
		});
	};

	const handleValidationRequest = (validate: () => Promise<boolean>) => {
		validationFunctionRef.current = validate;
	};

	const styles = categoryStyles[data.category];

	return (
		<>
			<div
				className={cn(
					'w-[280px] px-4 py-3 shadow-md rounded-md bg-white dark:bg-gray-800',
					'border-2 border-l-4 border-stone-200 dark:border-gray-700',
					styles.border,
					'relative',
					'transition-colors duration-200',
					styles.background,
					'hover:shadow-lg'
				)}
			>
				{canHaveInputs && (
					<Handle
						type="target"
						position={Position.Top}
						className={cn('w-3 h-3', styles.border.replace('border-l-', 'bg-'))}
					/>
				)}

				{/* Completion Indicator */}
				{isComplete && (
					<div className="flex absolute -top-2 -right-2 justify-center items-center w-6 h-6 bg-green-500 rounded-full shadow-md dark:bg-green-600">
						<Check className="w-4 h-4 text-white" />
					</div>
				)}

				<div className="flex justify-between items-center">
					<div className="flex flex-1 items-center pr-2 min-w-0">
						<div
							className={cn(
								'rounded-full w-8 h-8 flex-shrink-0 flex items-center justify-center',
								styles.icon
							)}
						>
							<Icon className="w-4 h-4 text-gray-700 dark:text-gray-300" />
						</div>
						<div className="flex-1 ml-2 min-w-0">
							<div className="text-sm font-bold text-gray-900 truncate dark:text-white">
								{currentData.label}
							</div>
							{currentData.description && (
								<div className="text-xs text-gray-500 truncate dark:text-gray-400">
									{currentData.description}
								</div>
							)}
						</div>
					</div>
					<div
						className="flex-shrink-0"
						onMouseEnter={handleMouseEnter}
						onMouseLeave={handleMouseLeave}
					>
						<button
							ref={buttonRef}
							className="p-1 text-gray-600 rounded transition-colors hover:bg-white/50 dark:hover:bg-gray-700/50 dark:text-gray-400"
							onClick={() => setActiveMenu(activeMenu === id ? null : id)}
						>
							<MoreVertical size={16} />
						</button>
						{activeMenu === id && (
							<div
								ref={menuRef}
								className="absolute right-0 z-10 py-1 mt-1 w-36 bg-white rounded-md border border-gray-200 shadow-lg dark:bg-gray-800 dark:border-gray-700"
							>
								<button
									className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
									onClick={handleEdit}
								>
									<Edit size={14} />
									Edit
								</button>
								<button
									className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
									onClick={handleCopy}
								>
									<Copy size={14} />
									Copy
								</button>
								<button
									className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-red-600 dark:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
									onClick={handleDelete}
								>
									<Trash2 size={14} />
									Delete
								</button>
							</div>
						)}
					</div>
				</div>

				{canHaveOutputs && (
					<Handle
						type="source"
						position={Position.Bottom}
						className={cn('w-3 h-3', styles.border.replace('border-l-', 'bg-'))}
					/>
				)}
			</div>

			<NodeDrawer
				isOpen={isDrawerOpen}
				onClose={() => setIsDrawerOpen(false)}
				nodeData={data}
				onSave={handleSave}
				onChange={handleChange}
				wizardMode={wizardState.wizardMode}
				currentStep={wizardState.currentStep}
				totalSteps={wizardState.totalSteps}
				canProceedToNext={wizardState.canProceedToNext}
				onPreviousStep={wizardState.onPreviousStep}
				onNextStep={wizardState.onNextStep}
				onCompleteWizard={wizardState.onCompleteWizard}
				validationAttempted={wizardState.validationAttempted}
				validationFailed={wizardState.validationFailed}
				isCompletingSetup={wizardState.isCompletingSetup}
				onHelpMenuOpen={() => setIsHelpMenuOpen(true)}
			>
				{ConfigComponent && (
					<ConfigComponent
						nodeId={id}
						data={data}
						onChange={handleChange}
						onWizardStateChange={setWizardState}
						onValidationRequest={handleValidationRequest}
						onClose={() => setIsDrawerOpen(false)}
					/>
				)}
			</NodeDrawer>
		</>
	);
}
